import { Component, Inject, PLATFORM_ID, ChangeDetectorRef, ViewChild } from '@angular/core';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { UserService } from '@app/services';
import { CommonModule } from '@angular/common';
import { User } from '@app/model/user';
import { AuthService } from '@app/services';
import { AnnouncementService } from '@app/services';
import { Announcement } from '@app/model/announcement';
import { isPlatformBrowser } from '@angular/common';
import { LocalStorageService } from '@app/services';
import { SideBarComponent } from '@app/shared/side-bar/side-bar.component';
import { NotificationBarComponent } from '@app/shared/notification-bar/notification-bar.component';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { FcmService } from '@app/services';
import { AppUpdateService } from '@app/services';
import { environment } from '../environments/environment';

@Component({
    selector: 'app-root',
    imports: [RouterOutlet, CommonModule, SideBarComponent, NotificationBarComponent],
    templateUrl: './app.component.html',
    // changeDetection: ChangeDetectionStrategy.OnPush
})

export class AppComponent {

  @ViewChild('notificationBar') notificationBarComponent?: NotificationBarComponent;

  title = 'Time-Web';
  user: User = new User('', '', '', '', '', '', '', 0, 0, 0, '', false, false, false, 0, '', '', '');
  announcements: Announcement[] = [];
  isLogin = false;
  isAdmin = false;
  staticAlertClosed = false;
  isFixed = false;
  isNarrow = false;
  scrollProgress = 0; // 滾動進度，用於漸進式動畫
  accountType: string = '';
  isCharity = false;
  fontSize: string = '18px';

  private updateInterval: any;
  private scrollTimer: any;
  private lastUpdateTime = 0;
  private readonly updateThrottleTime = 60000; // 60 秒節流
  private visibilityChangeListener: any;
  private onlineListener?: () => void;
  private offlineListener?: () => void;
  private networkRecoveryTimer: any;
  private networkRecoveryCheckId = 0;
  private networkProbeInterval: any;
  private fcmForegroundSubscription?: Subscription;
  private fcmRegisteredUserId: string | null = null;

  // Add these properties to your component class
  public isPageLoading: boolean = true;

  sidebarOpen = false;
  notificationOpen = false;
  hideNavigation = false;  // 新增屬性來控制導航欄顯示
  isOffline = false;
  unreadNotificationCount = 0;  // 新增屬性來追蹤未讀通知數量
  isSmartUpdating = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private userService: UserService,
    private router: Router,
    private authService: AuthService,
    private announcementService: AnnouncementService,
    private localStorageService: LocalStorageService,
    private fcmService: FcmService,
    private appUpdateService: AppUpdateService,
    private cdr: ChangeDetectorRef
  ) {}

  async ngOnInit() {
    this.appUpdateService.initialize();

    if (isPlatformBrowser(this.platformId)) {
      const storedFontSize = this.localStorageService.getItem<string | number>('fontSize', 18);
      this.applyAppFontSize(storedFontSize);
    }

    this.setupNetworkLock();

    // 監聽路由變化來控制導航欄顯示
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(async (event: NavigationEnd) => {
      this.hideNavigation = this.isAuthRoute(event.urlAfterRedirects);

      if (this.hideNavigation) {
        this.sidebarOpen = false;
        this.notificationOpen = false;
      }
      
      // 當路由到非登入/註冊頁面時，重新檢查用戶會話和更新數據
      if (!this.hideNavigation && !this.isOffline) {
        await this.initializeUserSession();
        // 如果用戶已登入，更新用戶數據
        if (this.isLogin) {
          await this.refreshUserData();
        }
      }
      
      this.cdr.markForCheck(); // 手動觸發變更檢測
    });

    // 初始檢查當前路由
    this.hideNavigation = this.isAuthRoute(this.router.url);

    // 設置頁面可見性監聽
    this.setupVisibilityListener();
    this.setupFcmForegroundListener();

    // 初始化用戶數據和會話
    if (!this.isOffline) {
      await this.initializeUserSession();
    }

    // Session 初始化完成後立即開放導覽，不額外延長等待時間。
    this.isPageLoading = false;

    // 設置智能更新定時器
    await this.smartUpdate();
    this.setupSmartUpdateInterval();
  }

  ngOnDestroy() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
    }
    if (this.scrollTimer) {
      clearTimeout(this.scrollTimer);
    }
    if (this.visibilityChangeListener && isPlatformBrowser(this.platformId)) {
      document.removeEventListener('visibilitychange', this.visibilityChangeListener);
    }
    if (this.onlineListener && isPlatformBrowser(this.platformId)) {
      window.removeEventListener('online', this.onlineListener);
    }
    if (this.offlineListener && isPlatformBrowser(this.platformId)) {
      window.removeEventListener('offline', this.offlineListener);
    }
    if (this.networkRecoveryTimer) {
      clearTimeout(this.networkRecoveryTimer);
    }
    if (this.networkProbeInterval) {
      clearInterval(this.networkProbeInterval);
    }
    this.fcmForegroundSubscription?.unsubscribe();
  }

  navigateTo(route: string) {
    if (!this.isPageLoading && !this.isOffline) {
      this.router.navigate([route]);
    }
  }

  // @HostListener('window:scroll', [])
  // onWindowScroll() {
  //   // 使用 requestAnimationFrame 來確保平順的動畫
  //   if (this.scrollTimer) {
  //     cancelAnimationFrame(this.scrollTimer);
  //   }
    
  //   this.scrollTimer = requestAnimationFrame(() => {
  //     const scrollY = window.scrollY;
      
  //     // 定義滾動範圍和漸進式閾值
  //     const startThreshold = 10;   // 開始縮放的位置
  //     const fullThreshold = 80;    // 完全縮放的位置
  //     const fixedThreshold = 15;   // 固定導航欄的位置
      
  //     // 計算滾動進度 (0-1 之間的值)
  //     this.scrollProgress = Math.min(Math.max((scrollY - startThreshold) / (fullThreshold - startThreshold), 0), 1);
      
  //     // 使用緩動函數讓動畫更自然
  //     const easeProgress = this.easeInOutCubic(this.scrollProgress);
      
  //     // 更新狀態
  //     const newIsFixed = scrollY > fixedThreshold;
  //     const newIsNarrow = easeProgress > 0.1; // 當動畫進度超過10%時開始縮放
      
  //     // 只有在狀態真正改變時才更新
  //     if (this.isFixed !== newIsFixed || this.isNarrow !== newIsNarrow) {
  //       this.isFixed = newIsFixed;
  //       this.isNarrow = newIsNarrow;
  //     }
  //   });
  // }
  
  // // 緩動函數：三次貝塞爾曲線，讓動畫更自然
  // private easeInOutCubic(t: number): number {
  //   return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  // }

  async toggleSideBar() {
    if (this.isOffline) return;

    const shouldOpen = !this.sidebarOpen;
    this.notificationOpen = false;
    this.sidebarOpen = shouldOpen;
    // 當打開側邊欄時，如果用戶已登入，刷新用戶數據
    if (this.sidebarOpen && this.isLogin) {
      await this.refreshUserData();
    }
  }

  closeSidebar() {
    this.sidebarOpen = false;
  }

  async toggleNotificationBar() {
    if (this.isOffline) return;

    const shouldOpen = !this.notificationOpen;
    this.sidebarOpen = false;
    this.notificationOpen = shouldOpen;
    // 當打開通知欄時，如果用戶已登入，刷新用戶數據和通知
    if (this.notificationOpen && this.isLogin) {
      await this.refreshUserData();
      if (this.user?.id) {
        await this.requestFcmPermissionAndRegister(this.user.id);
      }
      // 使用 ViewChild 直接調用 NotificationBar 的刷新方法
      if (this.notificationBarComponent) {
        await this.notificationBarComponent.refreshNotifications();
      }
    }
  }

  closeNotificationBar() {
    this.notificationOpen = false;
  }

  private isAuthRoute(url: string) {
    const path = url.split(/[?#]/, 1)[0];
    return path === '/login' || path === '/register';
  }

  onFontSizeChange(size: string) {
    this.applyAppFontSize(size);
  }

  private applyAppFontSize(size: string | number) {
    const parsedSize = Number.parseFloat(String(size));
    const normalizedSize = Number.isFinite(parsedSize)
      ? Math.min(Math.max(parsedSize, 16), 26)
      : 18;

    this.fontSize = `${normalizedSize}px`;

    if (isPlatformBrowser(this.platformId)) {
      document.documentElement.style.setProperty('--app-font-size', this.fontSize);
      document.documentElement.style.setProperty('--app-font-scale', String(normalizedSize / 18));
      this.localStorageService.setItem('fontSize', normalizedSize);
    }
  }

  async onSwitchAccount() {
    this.fcmRegisteredUserId = null;
    this.unreadNotificationCount = 0;
    await this.initializeUserSession();
    if (this.isLogin && this.user?.id) {
      await this.refreshUserData();
      await this.initializeFcmForUser(this.user.id);
      if (this.notificationBarComponent) {
        await this.notificationBarComponent.refreshNotifications();
      }
    }
    this.cdr.markForCheck();
  }

  onNotificationsUpdated(notifications: any[]) {
    this.unreadNotificationCount = notifications.length;
  }

  // 效能優化相關方法
  private setupVisibilityListener() {
    if (!isPlatformBrowser(this.platformId)) return;
    
    this.visibilityChangeListener = async () => {
      if (this.isOffline) {
        return;
      }

      if (document.visibilityState === 'visible') {
        // 頁面重新可見時立即更新一次
        await this.smartUpdate();
        if (!this.updateInterval) {
          this.setupSmartUpdateInterval();
        }
        
        // 如果用戶已登入，同時刷新用戶數據和通知
        if (this.isLogin) {
          await this.refreshUserData();
          if (this.notificationBarComponent) {
            await this.notificationBarComponent.refreshNotifications();
          }
        }
      } else {
        // 頁面隱藏時停止定時器節省資源
        if (this.updateInterval) {
          clearInterval(this.updateInterval);
          this.updateInterval = null;
        }
      }
    };
    
    document.addEventListener('visibilitychange', this.visibilityChangeListener);
  }

  private setupFcmForegroundListener() {
    if (!isPlatformBrowser(this.platformId)) return;

    this.fcmForegroundSubscription = this.fcmService.foregroundMessage$.subscribe(async (message) => {
      if (!message || !this.isLogin) return;

      if (this.notificationBarComponent) {
        await this.notificationBarComponent.refreshNotifications();
      }
    });
  }

  private setupNetworkLock() {
    if (!isPlatformBrowser(this.platformId)) return;

    const lockOffline = () => {
      this.networkRecoveryCheckId++;
      if (this.networkRecoveryTimer) {
        clearTimeout(this.networkRecoveryTimer);
        this.networkRecoveryTimer = null;
      }

      this.isOffline = true;
      this.sidebarOpen = false;
      this.notificationOpen = false;
      this.isPageLoading = false;
      this.cdr.markForCheck();
    };

    const scheduleOnlineRecovery = () => {
      const checkId = ++this.networkRecoveryCheckId;

      if (this.networkRecoveryTimer) {
        clearTimeout(this.networkRecoveryTimer);
      }

      this.networkRecoveryTimer = setTimeout(async () => {
        if (checkId !== this.networkRecoveryCheckId || !navigator.onLine) {
          return;
        }

        const hasConnection = await this.hasNetworkConnection();
        if (!hasConnection) {
          lockOffline();
          return;
        }

        const wasOffline = this.isOffline;
        this.isOffline = false;

        if (wasOffline) {
          await this.initializeUserSession();
          if (this.isLogin) {
            await this.refreshUserData();
          }
        }

        this.cdr.markForCheck();
      }, 1500);
    };

    this.onlineListener = scheduleOnlineRecovery;
    this.offlineListener = lockOffline;

    window.addEventListener('online', this.onlineListener);
    window.addEventListener('offline', this.offlineListener);
    this.networkProbeInterval = setInterval(() => {
      if (this.isOffline && navigator.onLine) {
        scheduleOnlineRecovery();
      }
    }, 5000);

    if (navigator.onLine) {
      this.isOffline = false;
      scheduleOnlineRecovery();
    } else {
      lockOffline();
    }
  }

  private async hasNetworkConnection(): Promise<boolean> {
    const checks = [
      this.fetchWithTimeout(`${window.location.origin}/?network_probe=${Date.now()}`, {
        method: 'HEAD',
        cache: 'no-store'
      }),
      this.fetchWithTimeout(`${environment.supabaseUrl}/auth/v1/settings?ts=${Date.now()}`, {
        method: 'GET',
        cache: 'no-store',
        headers: {
          apikey: environment.supabaseKey
        }
      })
    ];

    const results = await Promise.allSettled(checks);
    return results.some((result) => result.status === 'fulfilled');
  }

  private async fetchWithTimeout(input: RequestInfo | URL, init: RequestInit, timeoutMs = 5000): Promise<void> {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

    try {
      await fetch(input, {
        ...init,
        signal: controller.signal
      });
    } finally {
      window.clearTimeout(timeoutId);
    }
  }

  private lockAppForNetworkFailure() {
    if (!isPlatformBrowser(this.platformId)) return;

    this.networkRecoveryCheckId++;
    this.isOffline = true;
    this.sidebarOpen = false;
    this.notificationOpen = false;
    this.isPageLoading = false;
    this.cdr.markForCheck();
  }

  private async initializeFcmForUser(userId: string) {
    if (!userId || this.fcmRegisteredUserId === userId) return;

    try {
      const token = await this.fcmService.initializeForUser(userId);
      if (token) {
        this.fcmRegisteredUserId = userId;
      }
    } catch (error) {
      console.warn('FCM 初始化失敗:', error);
    }
  }

  private async requestFcmPermissionAndRegister(userId: string) {
    if (!userId || this.fcmRegisteredUserId === userId) return;

    try {
      const token = await this.fcmService.requestPermissionAndRegister(userId);
      if (token) {
        this.fcmRegisteredUserId = userId;
      }
    } catch (error) {
      console.warn('FCM 權限或 token 註冊失敗:', error);
    }
  }

  private async initializeUserSession() {
    try {
      const session = await this.authService.getSession();

      if (session) {
        this.isLogin = true;
        await this.loadUserData();
      } else {
        this.isLogin = false;
        this.unreadNotificationCount = 0;
      }
    } catch (error) {
      console.error('Session initialization error:', error);
    }
  }

  private async loadUserData() {
    try {
      const userRes = await this.userService.getUser();
      if (userRes && userRes.data && userRes.data.length > 0) {
        const user = userRes.data[0];
        this.user = user;
        this.isAdmin = user.is_admin;
        this.accountType = user.account_type || '';
        this.isCharity = user.is_charity || false;
        await this.initializeFcmForUser(user.id);
        this.cdr.markForCheck();
      } else if (userRes?.error) {
        console.error('獲取用戶資料失敗：', userRes.error);
        this.lockAppForNetworkFailure();
      }
    } catch (error) {
      console.error('Load user data error:', error);
      this.lockAppForNetworkFailure();
    }
  }

  // 新增方法：刷新用戶數據（用於路由變化時更新）
  private async refreshUserData() {
    try {
      const userRes = await this.userService.getUser();
      if (userRes && userRes.data && userRes.data.length > 0) {
        const user = userRes.data[0];
        // 更新用戶數據，特別是雲幣餘額等動態數據
        this.user = user;
        this.isAdmin = user.is_admin;
        this.accountType = user.account_type || '';
        this.isCharity = user.is_charity || false;
        await this.initializeFcmForUser(user.id);
        this.cdr.markForCheck();
      }
    } catch (error) {
      console.error('刷新用戶數據失敗:', error);
      this.lockAppForNetworkFailure();
    }
  }

  private setupSmartUpdateInterval() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
    }

    this.updateInterval = setInterval(async () => {
      await this.smartUpdate();
    }, this.updateThrottleTime);
  }

  private async smartUpdate() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // 節流控制
    const now = Date.now();
    if (now - this.lastUpdateTime < this.updateThrottleTime) {
      return;
    }

    // 只在頁面可見且沒有其他檢查在進行時執行
    if (this.isOffline || document.visibilityState !== 'visible' || this.isSmartUpdating) {
      return;
    }

    this.lastUpdateTime = now;
    this.isSmartUpdating = true;

    try {
      const announcements = await this.announcementService.getAllAnnouncements();
      this.announcements = announcements || [];

      // 批量觸發變更檢測
      this.cdr.markForCheck();

    } catch (error) {
      console.error('Smart update error:', error);
    } finally {
      this.isSmartUpdating = false;
    }
  }
}
